import {
  Dimensions,
  useWindowDimensions,
  type AccessibilityProps,
} from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { type LegacyPanGesture } from "@-/com.npmjs.react-native-gesture-handler";
import type { DerivedValue } from "@-/com.npmjs.react-native-reanimated";
import { useTopSheetRevealMotion } from "@-/dev.improve.haniverse.components.ui.top-sheet";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  FLOATING_TAB_BAR_BOTTOM_GAP_PX,
  PRIMARY_NAVIGATION_STRIP_FLOATING_TABS_HEIGHT_PX,
  getAndroidUsesButtonNavBar,
  getPrimaryNavigationBarHeight,
  useHasPrimaryNavigationStripFloatingTabs,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useInternalFeatureAccess } from "@-/dev.improve.haniverse.lib.admin.access";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { type ReactNode, useState } from "react";
import { ADMIN_PANEL_HANDLE_HEIGHT, type AdminPanelTab } from "#admin-panel-layout";
import { AdminPanelSheet } from "#admin-panel-sheet";

const ADMIN_GESTURE_ACTION = {
  hint: "Swipe down to open the admin panel",
  label: "Open admin panel",
  name: "admin",
};

/**
 * Owns the one panel and its drawer-button drag. Render the gesture detector on the button, and
 * this owner at the app-shell root so the Android overlay has window-sized, untransformed bounds.
 */
export function AdminPanelGesture({
  children,
  renderDrawerButton,
  shellTranslateX,
}: {
  children: (props: AccessibilityProps, gesture: LegacyPanGesture) => ReactNode;
  /**
   * The drawer button again, drawn on a top-placed panel over the corner it covers. Attach `grip`
   * to it so dragging it resizes the panel the way dragging the real button reveals it.
   */
  renderDrawerButton?: (grip: LegacyPanGesture) => ReactNode;
  /** The app shell's horizontal offset while the profile drawer slides it aside. */
  shellTranslateX?: DerivedValue<number>;
}) {
  const enabled = useInternalFeatureAccess();
  const [isPresented, setIsPresented] = useState(false);
  const [placement, setPlacement] = useDeviceSetting("adminPanelPlacement");
  // Reopening returns to the subview that was showing.
  const [tab, setTab] = useState<AdminPanelTab>("level");
  const hasFloatingTabs = useHasPrimaryNavigationStripFloatingTabs();
  const { height: windowHeight } = useWindowDimensions();
  const { bottom, top } = useSafeAreaInsets();
  const { height: toolbarHeight, isLandscape } = useToolbarGridMetrics();
  const bottomChromeHeight = isLandscape
    ? bottom
    : getPrimaryNavigationBarHeight(bottom, {
        androidUsesButtonNavBar: getAndroidUsesButtonNavBar({
          insetsBottom: bottom,
          screenHeight: Dimensions.get("screen").height,
          windowHeight,
        }),
      });
  // At the top the sheet covers the app's toolbar row with its own and stops above the strip. At
  // the bottom it rests above the strip and any floating page tabs (leaving the same gap above
  // their platter as below it), and stops below the app's toolbar row, which stays in view.
  const bottomEdgeY =
    windowHeight -
    bottomChromeHeight -
    (hasFloatingTabs
      ? PRIMARY_NAVIGATION_STRIP_FLOATING_TABS_HEIGHT_PX + FLOATING_TAB_BAR_BOTTOM_GAP_PX
      : 0);
  const headerHeight =
    placement === "top"
      ? top + toolbarHeight + ADMIN_PANEL_HANDLE_HEIGHT
      : ADMIN_PANEL_HANDLE_HEIGHT + toolbarHeight;
  const maximum = Math.max(
    headerHeight,
    placement === "top" ? windowHeight - bottomChromeHeight : bottomEdgeY - (top + toolbarHeight),
  );
  const motion = useTopSheetRevealMotion({
    anchorY: bottomEdgeY,
    edge: placement,
    enabled,
    headerHeight,
    maximum,
    onClosed: () => setIsPresented(false),
    onOpen: () => setIsPresented(true),
  });
  return (
    <>
      {children(
        enabled
          ? {
              accessibilityActions: [
                { label: ADMIN_GESTURE_ACTION.label, name: ADMIN_GESTURE_ACTION.name },
              ],
              accessibilityHint: ADMIN_GESTURE_ACTION.hint,
              onAccessibilityAction: ({ nativeEvent }) => {
                if (nativeEvent.actionName === ADMIN_GESTURE_ACTION.name) {
                  motion.open();
                }
              },
            }
          : {},
        motion.trigger,
      )}
      {enabled && isPresented ? (
        <AdminPanelSheet
          bottomEdgeY={bottomEdgeY}
          motion={motion}
          onPlacementChange={setPlacement}
          onTabChange={setTab}
          placement={placement}
          renderDrawerButton={renderDrawerButton}
          shellTranslateX={shellTranslateX}
          tab={tab}
        />
      ) : null}
    </>
  );
}
